/** The drawing of `townProps.ts`: the clutter between the buildings.
 *
 * A prop takes the same hi-def contract a building takes (townSlots.TOWN_HD):
 * the sprite ships at 1.5x the size it is drawn at on a 1080p stage, the box
 * is the sprite's own size, and the transform scales it down about its BOTTOM
 * CENTRE so the prop's foot lands on its ground line whatever its scale is.
 *
 * Its own basename: `townProps.tsx` beside `townProps.ts` resolves the same
 * specifier under two different bundlers, which is the bug that made
 * `townTracksView.tsx` exist.
 */

import type { CSSProperties } from "react";
import { ScaledSprite, useSpriteSize } from "./primitives.js";
import type { TownProp } from "./townProps.js";
import { BAND_HAZE, TOWN_HD } from "./townSlots.js";
import type { TownSlot } from "./townSlots.js";

/** The band's air, the same chain the buildings take (TownScreen.haze). */
function propHaze(band: TownSlot["band"]): string | undefined {
  const h = BAND_HAZE[band];
  if (h <= 0) return undefined;
  return `saturate(${String(1 - 0.22 * h)}) contrast(${String(1 - 0.04 * h)}) `
    + `brightness(${String(1 + 0.02 * h)})`;
}

export function TownPropHD(props: { readonly prop: TownProp }): JSX.Element | null {
  const p = props.prop;
  const size = useSpriteSize(p.art, 1);
  const fw = size?.w ?? Math.round(p.w * TOWN_HD);
  const fh = size?.h ?? Math.round(p.h * TOWN_HD);
  if (fw <= 0 || fh <= 0) return null;
  // The whole magnification is in the transform: the drawn width the layout
  // asked for over the width the sprite actually shipped at.
  const k = p.w / fw;
  const filter = propHaze(p.band);
  const style: CSSProperties = {
    left: `${String(Math.round(p.x - fw / 2))}px`,
    top: `${String(Math.round(p.y - fh))}px`,
    width: `${String(fw)}px`,
    height: `${String(fh)}px`,
    transform: `scale(${k.toFixed(6)})${p.flip ? " scaleX(-1)" : ""}`,
    ...(filter === undefined ? {} : { filter }),
  };
  return (
    <span className="hs-town__prop hs-town__prop--hd" style={style} aria-hidden="true">
      <ScaledSprite atlasKey={p.art} scale={1} className="hs-town__art" />
    </span>
  );
}

/** The layer's own rules, mounted by TownScreen beside its paint styles. */
export const PROPS_CSS = `
/* Painterly props on a painterly backdrop: resampled smoothly, like the
   backdrop and the buildings, and never pixelated by the map's rules. */
.hs-town .hs-town__canvas .hs-town__prop--hd { image-rendering: auto; }
.hs-town .hs-town__canvas .hs-town__prop--hd .hs-town__art {
  image-rendering: auto;
  /* Softer than a building's hard offset silhouette: a barrel is 20 canvas
     pixels wide and the buildings' 8px drop shadow on it is a second barrel. */
  filter: var(--hs-roof, brightness(1))
    drop-shadow(4px 3px 1px rgba(24,18,10,0.42));
}
`;
